/** @format */
import {useCallback, useEffect, useState} from 'react';

import {Box, LinearProgress, Typography} from '@mui/material';
import Alert from '@mui/material/Alert';
import {Trans, useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';

import InformationDialog from 'components/dialogs/InformationDialog';

import DemoModal from 'components/DemoModal';
import WizardSidebar from 'components/MediaplanSidebar/WizardSidebar';

import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';

import {useMixpanelContext} from 'contexts/MixpanelContext';

import {authUrl} from 'helpers/connectHelpers';
import {useCallbackOnReturn} from 'helpers/routerHelpers';

import {OauthProvider, OauthProvider as Provider} from 'graphql/generated/types';

import NameStep from '../../NameStep';
import StepWrapper from '../../StepWrapper';
import TokenStep from '../../TokenStep';
import AdSystemConnectBlock from '../../components/AdSystemConnectBlock';
import AnalyticsSettings from '../components/AnalyticsSettings';
import MetrikaConnectBlock, {MetrikaStepHeader} from '../../components/MetrikaConnectBlock';

import {STATS_PROVIDERS} from '../Created';
import {
  useDeleteProviderTokenMutation,
  useDisconnectProviderMutation,
  useGetCurrentUserQuery,
  useSetWizardFinishedMutation,
  useStartMutation,
  useUpdateCompanyMutation,
} from './graphql/__generated__';

const WizardCreated = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useTranslation();

  const {mixpanel} = useMixpanelContext();

  let [searchParams, setSearchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');
  const confirm = searchParams.get('confirm');

  const [isOnConfirmFired, setIsOnConfirmFired] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [saveCompanyError, setSaveCompanyError] = useState('');
  const [activeProvider, setActiveProvider] = useState<Provider | null>('YANDEX');
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);

  const [showDemoModal, setShowDemoModal] = useState(false);

  const {loading, error, data, refetch} = useGetCurrentUserQuery({
    variables: {id: companyId},
  });

  const [updateCompany] = useUpdateCompanyMutation();
  const [disconnectProvider, {loading: loadingDisconnectProvider}] =
    useDisconnectProviderMutation();
  const [deleteProviderToken] = useDeleteProviderTokenMutation();
  const [start] = useStartMutation();
  const [setWizardFinished] = useSetWizardFinishedMutation();

  useCallbackOnReturn(refetch);

  const saveWithMediaplan = async () => {
    await handleFinishQueries();

    navigate(`/home/project/type?companyId=${companyId}`);
  };

  const renderFooterButtons = () => {
    if (!tokensHaveAccounts) return null;

    return (
      <Box
        sx={{
          mt: 24,
          mb: 24,
        }}
      >
        <PrimaryMediumButton onClick={saveWithMediaplan}>
          <Trans>Перейти к настройке медиаплана</Trans>
        </PrimaryMediumButton>
        <PrimaryMediumButton
          onClick={handleFinishAndNavigate}
          sx={{ml: 2, backgroundColor: '#202020'}}
        >
          <Trans>Сохранить без медиаплана</Trans>
        </PrimaryMediumButton>
      </Box>
    );
  };

  const deleteConfirm = useCallback(() => {
    if (searchParams.has('confirm')) {
      searchParams.delete('confirm');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!data) return;

    const company = data.currentUser?.company;

    if (company?.tokens.length === 0) return;

    const token = company?.tokens.find(token => token.provider === confirm);

    if (token) {
      if (isOnConfirmFired) return;

      const {provider, lastError} = token;
      const isConnected = lastError == null;

      if (!isConnected) return;

      setIsOnConfirmFired(true);

      deleteConfirm();

      navigate(`subaccounts?companyId=${companyId}&provider=${provider}`, {
        state: {background: location},
      });
    }
  }, [
    data,
    confirm,
    companyId,
    location,
    navigate,
    isOnConfirmFired,
    setSearchParams,
    searchParams,
    deleteConfirm,
  ]);

  useEffect(() => {
    if (!data) return;

    const company = data.currentUser?.company;

    const token = company?.tokens.find(token => token.provider === confirm);
    const {lastError} = token || {};

    if (token && lastError) {
      setShowErrorDialog(true);
    }
  }, [data, confirm]);

  useEffect(() => {
    if (!data) return;

    const company = data.currentUser?.company;
    setProjectName(company?.name || '');
  }, [data]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading || loadingDisconnectProvider) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const company = data?.currentUser?.company;
  const roles = data?.currentUser?.roles;
  const tokens = company?.tokens || [];

  const adTokens = tokens.filter(token => !STATS_PROVIDERS.includes(token.provider));
  const statTokens = tokens.filter(token => STATS_PROVIDERS.includes(token.provider));

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setProjectName(e.target.value);

      mixpanel.track('click_wizard_update_company_button');

      if (companyId) {
        await updateCompany({
          variables: {id: companyId, name: e.target.value},
        });
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error.';
      setSaveCompanyError(message);
    }
  };

  const getState = (provider: Provider) => {
    const url = new URL(window.location.href);
    url.searchParams.append('confirm', provider);

    const returnUrl = url.toString();
    const state = {companyId, returnUrl};

    return window.btoa(JSON.stringify(state));
  };

  const handleAdConnect = (provider: Provider) => {
    if (roles?.includes('DEMO')) {
      setShowDemoModal(true);
      return;
    }

    mixpanel.track('click_wizard_before_connect_continue_button', {
      provider,
    });

    const authUrlResult = authUrl(provider, getState(provider));

    if (!authUrlResult) return;

    window.location.href = authUrlResult.href;
  };

  const handleEditSubbaccounts = (provider: Provider) => {
    if (roles?.includes('DEMO')) {
      setShowDemoModal(true);
      return;
    }

    navigate(`subaccounts?companyId=${companyId}&provider=${provider}`, {
      state: {background: location},
    });
  };

  const handleRemoveToken = async (provider: Provider) => {
    if (roles?.includes('DEMO')) {
      setShowDemoModal(true);
      return;
    }

    mixpanel.track('click_disconnect_button', {provider: provider});

    if (companyId) {
      await disconnectProvider({
        variables: {provider: provider as OauthProvider, companyId},
      });
    }

    refetch();
  };

  const renderErrorDialog = () => {
    if (!data) return;

    const token = company?.tokens.find(token => token.provider === confirm);

    if (!token) return;

    const handleClose = async () => {
      setShowErrorDialog(false);

      if (companyId) {
        await deleteProviderToken({
          variables: {provider: token.provider, companyId: companyId},
        });
      }

      deleteConfirm();

      refetch();
    };

    return (
      <InformationDialog
        open={showErrorDialog}
        onClose={handleClose}
        title={t(
          'Ошибка при подключении рекламной кампании',
          'Ошибка при подключении рекламной кампании',
        )}
        body={token.lastError}
      />
    );
  };

  const handleCloseDemoModal = () => {
    setShowDemoModal(false);
  };

  const handleFinishQueries = async () => {
    if (!data) return;

    const isWizardFinished = data.currentUser?.isWizardFinished;

    if (!isWizardFinished) {
      await setWizardFinished();
    }

    if (companyId) {
      await start({
        variables: {
          companyId,
        },
      });
    }
  };

  const handleFinishAndNavigate = async () => {
    await handleFinishQueries();

    navigate('/');
  };

  const tokensHaveAccounts = tokens.length > 0 ? tokens.every(t => t.accounts.length > 0) : false;

  return (
    <>
      <WizardSidebar activeStep={'CREATE'} />

      <Typography variant='h1'>
        <Trans>Редактирование проекта</Trans>
      </Typography>

      <NameStep
        onChange={handleChange}
        saveCompanyError={saveCompanyError}
        projectName={projectName}
        isProjectCreated
      />

      <StepWrapper
        header={
          <>
            2. <Trans>Рекламная система</Trans>
          </>
        }
      >
        {adTokens.length > 0 ? (
          adTokens.map(token => (
            <TokenStep
              key={token.provider}
              token={token as any}
              onEditSubbaccounts={handleEditSubbaccounts}
              onRemoveToken={handleRemoveToken}
              onAdConnect={handleAdConnect}
            />
          ))
        ) : (
          <AdSystemConnectBlock
            provider={activeProvider ?? 'YANDEX'}
            onConnect={handleAdConnect}
            activeProvider={activeProvider}
            onProviderClick={setActiveProvider}
          />
        )}
      </StepWrapper>

      {!activeProvider && (
        <Box sx={{mt: 12}}>
          <Typography>
            <Trans>Без рекламной системы проект будет ограничен в функционале</Trans>
          </Typography>
        </Box>
      )}
      {(adTokens.length > 0 || !activeProvider) && (
        <StepWrapper header={<MetrikaStepHeader />}>
          {statTokens.length > 0 ? (
            statTokens.map(token => (
              <TokenStep
                key={token.provider}
                token={token as any}
                onEditSubbaccounts={handleEditSubbaccounts}
                onRemoveToken={handleRemoveToken}
                onAdConnect={handleAdConnect}
                isAnalyticsStep
              />
            ))
          ) : (
            <MetrikaConnectBlock onConnect={handleAdConnect} />
          )}
          {statTokens.some(t => t.provider === 'METRIKA') && companyId && (
            <AnalyticsSettings companyId={companyId} />
          )}
        </StepWrapper>
      )}

      {renderFooterButtons()}

      {renderErrorDialog()}

      {showDemoModal && <DemoModal onClose={handleCloseDemoModal} />}
    </>
  );
};

export default WizardCreated;
